import { apiClient } from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export type TaskStatus =
  | 'TODO'
  | 'IN_PROGRESS'
  | 'ON_HOLD'
  | 'IN_REVIEW'
  | 'IN_APPROVAL'
  | 'NEEDS_REWORK'
  | 'DONE'
  | 'CANCELLED';

export type TaskPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export interface TaskUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  role?: string;
}

export interface TaskCategory {
  id: string;
  name: string;
  color: string;
}

export interface TaskListItem {
  id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  salonId: string;
  startAt: string | null;
  startHasTime: boolean;
  dueAt: string | null;
  dueHasTime: boolean;
  estimatedMinutes: number | null;
  trackedSeconds: number;
  requiresApproval: boolean;
  isArchived: boolean;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  category: TaskCategory | null;
  createdBy: TaskUser;
  assignees: TaskUser[];
  checklist: { total: number; done: number; percent: number | null };
  commentCount: number;
  attachmentCount: number;
  isOverdue: boolean;
}

export interface ChecklistItem {
  id: string;
  content: string;
  isDone: boolean;
  position: number;
  doneAt: string | null;
  doneById: string | null;
}

export interface TaskTimeSession {
  id: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  note: string | null;
  endReason: 'PAUSE' | 'STOP' | 'AUTO' | 'MANUAL' | null;
  isManual: boolean;
  editedById: string | null;
  editedAt: string | null;
  editReason: string | null;
  taskId: string;
  userId: string;
  user?: TaskUser;
}

export interface TaskAttachment {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  isImage: boolean;
  createdAt: string;
  uploaderId: string;
  commentId?: string | null;
  uploader?: TaskUser;
}

export interface TaskComment {
  id: string;
  body: string;
  isRework: boolean;
  mentions: string[] | null;
  editedAt: string | null;
  createdAt: string;
  authorId: string;
  author: TaskUser;
  attachments: TaskAttachment[];
}

export interface TaskActivity {
  id: string;
  type: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  note: string | null;
  createdAt: string;
  actor: TaskUser | null;
}

export interface TaskPermissions {
  canEdit: boolean;
  canManage: boolean;
  canParticipate: boolean;
  canComment: boolean;
  canUpload: boolean;
  canRunTimer: boolean;
  canApprove: boolean;
  availableStatuses: TaskStatus[];
}

export interface TaskDetail extends Omit<TaskListItem, 'checklist' | 'assignees'> {
  description: string | null;
  assignees: (TaskUser & { assignedAt: string })[];
  checklist: ChecklistItem[];
  checklistProgress: { total: number; done: number; percent: number | null };
  approvedBy: TaskUser | null;
  approvedAt: string | null;
  rejectedBy: TaskUser | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  archivedAt: string | null;
  sessions: TaskTimeSession[];
  activeSession: TaskTimeSession | null;
  myTrackedSeconds: number;
  permissions: TaskPermissions;
}

export interface SalonMember {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: string;
  staffId: string | null;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface BoardColumn {
  status: TaskStatus;
  label: string;
  total: number;
  tasks: TaskListItem[];
}

export interface TimerStatus {
  serverNow: string;
  session:
    | (TaskTimeSession & {
        elapsedSeconds: number;
        task: { id: string; title: string; status: TaskStatus; salonId: string; trackedSeconds: number };
      })
    | null;
}

export interface TaskNotificationSettings {
  inAppAssigned: boolean;
  inAppDueChanged: boolean;
  inAppComment: boolean;
  inAppMention: boolean;
  inAppDueSoon: boolean;
  inAppOverdue: boolean;
  inAppWorkflow: boolean;
  emailAssigned: boolean;
  emailDueSoon: boolean;
  emailWorkflow: boolean;
  dueSoonHours: number;
}

export interface TaskStats {
  range: { from: string; to: string; granularity: string };
  summary: {
    openTasks: number;
    overdue: number;
    pendingReview: number;
    pendingApproval: number;
    needsRework: number;
    completedInRange: number;
    createdInRange: number;
    reworkCount: number;
    onTime: {
      completedWithDueDate: number;
      onTime: number;
      withoutDueDate: number;
      percent: number | null;
    };
    avgCompletionHours: number | null;
    avgTimeToStartHours: number | null;
    timeToStartSampleSize: number;
    avgApprovalWaitHours: number | null;
    approvalWaitSampleSize: number;
    trackedSeconds: number;
    trackedSessions: number;
  };
  byStatus: { status: TaskStatus; count: number }[];
  byPriority: { priority: TaskPriority; count: number }[];
  byCategory: { categoryId: string | null; name: string; color: string; count: number }[];
  estimateVsActual: {
    taskCount: number;
    withoutEstimate: number;
    estimatedMinutes: number;
    actualMinutes: number;
  };
  trend: { bucket: string; created: number; completed: number }[];
  byEmployee?: {
    userId: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    role: string;
    openTasks: number;
    completedInRange: number;
    completedWithDueDate: number;
    onTime: number;
    onTimePercent: number | null;
    avgCompletionHours: number | null;
    trackedSeconds: number;
  }[];
  definitions: Record<string, string>;
}

export interface TaskFilters {
  salonId?: string;
  status?: TaskStatus[];
  priority?: TaskPriority[];
  assigneeId?: string;
  categoryId?: string;
  search?: string;
  dueFrom?: string;
  dueTo?: string;
  overdue?: boolean;
  pendingApproval?: boolean;
  withActiveTimer?: boolean;
  archived?: boolean;
  mine?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  columnLimit?: number;
}

/** Axios serialises arrays as `status[]=`; the API expects a comma list. */
function toParams(filters: TaskFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === false) return;
    params[key] = Array.isArray(value) ? value.join(',') : value;
  });
  return params;
}

// ─── API ────────────────────────────────────────────────────────────────────

export const taskApi = {
  context: () =>
    apiClient.get<{ isManager: boolean; salonIds: string[] | null }>('/tasks/context').then((r) => r.data),
  members: (salonId?: string) =>
    apiClient.get<SalonMember[]>('/tasks/members', { params: { salonId } }).then((r) => r.data),

  listCategories: (salonId?: string) =>
    apiClient.get<TaskCategory[]>('/tasks/categories', { params: { salonId } }).then((r) => r.data),
  createCategory: (data: { salonId?: string; name: string; color?: string }) =>
    apiClient.post<TaskCategory>('/tasks/categories', data).then((r) => r.data),
  updateCategory: (id: string, data: { name?: string; color?: string }) =>
    apiClient.put<TaskCategory>(`/tasks/categories/${id}`, data).then((r) => r.data),
  deleteCategory: (id: string) => apiClient.delete(`/tasks/categories/${id}`).then((r) => r.data),

  list: (filters: TaskFilters) =>
    apiClient
      .get<{ data: TaskListItem[]; pagination: Pagination }>('/tasks', { params: toParams(filters) })
      .then((r) => r.data),
  board: (filters: TaskFilters) =>
    apiClient
      .get<{ columns: BoardColumn[] }>('/tasks/board', { params: toParams(filters) })
      .then((r) => r.data),
  counts: (salonId?: string) =>
    apiClient
      .get<{ mine: number; today: number; upcoming: number; overdue: number; review: number; approval: number }>(
        '/tasks/counts',
        { params: { salonId } },
      )
      .then((r) => r.data),

  get: (id: string) => apiClient.get<TaskDetail>(`/tasks/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => apiClient.post<TaskListItem>('/tasks', data).then((r) => r.data),
  update: (id: string, data: Record<string, unknown>) =>
    apiClient.put<TaskListItem>(`/tasks/${id}`, data).then((r) => r.data),
  changeStatus: (id: string, status: TaskStatus, options: { reason?: string; stopTimer?: boolean } = {}) =>
    apiClient.patch<TaskListItem>(`/tasks/${id}/status`, { status, ...options }).then((r) => r.data),
  archive: (id: string) => apiClient.post(`/tasks/${id}/archive`).then((r) => r.data),
  unarchive: (id: string) => apiClient.post(`/tasks/${id}/unarchive`).then((r) => r.data),
  remove: (id: string) => apiClient.delete(`/tasks/${id}`).then((r) => r.data),
  bulk: (data: Record<string, unknown>) =>
    apiClient.post<{ succeeded: number; failed: { id: string; error: string }[] }>('/tasks/bulk', data)
      .then((r) => r.data),

  activity: (id: string, page = 1, limit = 50) =>
    apiClient
      .get<{ data: TaskActivity[]; pagination: Pagination }>(`/tasks/${id}/activity`, { params: { page, limit } })
      .then((r) => r.data),

  listComments: (id: string, page = 1, limit = 30) =>
    apiClient
      .get<{ data: TaskComment[]; pagination: Pagination }>(`/tasks/${id}/comments`, { params: { page, limit } })
      .then((r) => r.data),
  addComment: (id: string, body: string, mentions: string[] = []) =>
    apiClient.post<TaskComment>(`/tasks/${id}/comments`, { body, mentions }).then((r) => r.data),
  updateComment: (commentId: string, body: string) =>
    apiClient.put<TaskComment>(`/tasks/comments/${commentId}`, { body }).then((r) => r.data),
  deleteComment: (commentId: string) => apiClient.delete(`/tasks/comments/${commentId}`).then((r) => r.data),

  addChecklistItem: (id: string, content: string) =>
    apiClient.post<ChecklistItem>(`/tasks/${id}/checklist`, { content }).then((r) => r.data),
  updateChecklistItem: (itemId: string, data: { content?: string; isDone?: boolean }) =>
    apiClient.patch<ChecklistItem>(`/tasks/checklist/${itemId}`, data).then((r) => r.data),
  deleteChecklistItem: (itemId: string) => apiClient.delete(`/tasks/checklist/${itemId}`).then((r) => r.data),

  listAttachments: (id: string) =>
    apiClient.get<TaskAttachment[]>(`/tasks/${id}/attachments`).then((r) => r.data),
  uploadAttachments: (
    id: string,
    files: File[],
    options: { commentId?: string; onProgress?: (percent: number) => void } = {},
  ) => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    if (options.commentId) form.append('commentId', options.commentId);
    return apiClient
      .post<TaskAttachment[]>(`/tasks/${id}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (options.onProgress && e.total) {
            options.onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      })
      .then((r) => r.data);
  },
  deleteAttachment: (attachmentId: string) =>
    apiClient.delete(`/tasks/attachments/${attachmentId}`).then((r) => r.data),
  /**
   * Attachments live in private storage, so they are fetched with the auth
   * header and turned into a short-lived blob URL — there is no public link.
   */
  fetchAttachmentBlob: (attachmentId: string, inline = false) =>
    apiClient
      .get(`/tasks/attachments/${attachmentId}/download`, {
        params: inline ? { inline: 'true' } : undefined,
        responseType: 'blob',
      })
      .then((r) => r.data as Blob),

  timerStatus: () => apiClient.get<TimerStatus>('/tasks/timer/status').then((r) => r.data),
  timerStart: (taskId: string, force = false) =>
    apiClient.post<TimerStatus>(`/tasks/${taskId}/timer/start`, { force }).then((r) => r.data),
  timerPause: (note?: string) => apiClient.post<TimerStatus>('/tasks/timer/pause', { note }).then((r) => r.data),
  timerStop: (note?: string) => apiClient.post<TimerStatus>('/tasks/timer/stop', { note }).then((r) => r.data),
  listSessions: (taskId: string, page = 1, limit = 50) =>
    apiClient
      .get<{
        data: TaskTimeSession[];
        totals: { totalSeconds: number; mySeconds: number; byUser: { userId: string; seconds: number }[] };
        pagination: Pagination;
      }>(`/tasks/${taskId}/sessions`, { params: { page, limit } })
      .then((r) => r.data),
  correctSession: (
    sessionId: string,
    data: { startedAt?: string; endedAt?: string; note?: string; reason: string },
  ) => apiClient.patch<TaskTimeSession>(`/tasks/timer/sessions/${sessionId}`, data).then((r) => r.data),
  deleteSession: (sessionId: string, reason: string) =>
    apiClient.delete(`/tasks/timer/sessions/${sessionId}`, { data: { reason } }).then((r) => r.data),

  salonStats: (params: Record<string, unknown>) =>
    apiClient.get<TaskStats>('/tasks/stats/salon', { params }).then((r) => r.data),
  myStats: (params: Record<string, unknown>) =>
    apiClient.get<TaskStats>('/tasks/stats/me', { params }).then((r) => r.data),

  getNotificationSettings: () =>
    apiClient
      .get<{ settings: TaskNotificationSettings; emailAvailable: boolean }>('/tasks/settings/notifications')
      .then((r) => r.data),
  updateNotificationSettings: (data: Partial<TaskNotificationSettings>) =>
    apiClient
      .put<{ settings: TaskNotificationSettings; emailAvailable: boolean }>('/tasks/settings/notifications', data)
      .then((r) => r.data),
};

/** Pulls the user-facing message (and optional code) out of an axios error. */
export function taskErrorMessage(err: unknown, fallback = 'Wystąpił błąd. Spróbuj ponownie.'): string {
  const e = err as { response?: { data?: { error?: string; details?: { message?: string }[] } } };
  const detail = e?.response?.data?.details?.[0]?.message;
  return detail || e?.response?.data?.error || fallback;
}

export function taskErrorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
}
